import React, { useState } from 'react';
import {
  Asset,
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  WarningIcon,
  ErrorIcon,
  YesIcon,
  Tooltip,
} from '@clearcover/atlas';

import cx from 'classnames';
import { useLazyApi } from 'Shared/services/Api';
import { useAuth0 } from 'Auth';
import { editVehicleInspectionPolicy } from 'Admin/authPolicies';
import {
  GetVehicleInspectionArtifactResponse,
  VehicleInspectionRequest as VehicleInspectionRequestType,
} from '@clearcover/core-app-types';

import { Asset as SharedAsset } from 'Shared/components';

import styles from './styles.module.css';
import { AcceptedStatusType, ChangeStatusType } from '../types';

interface ViewMediaComponentProps {
  artifactId: string | null;
  requestId: string;
  onChangeStatus: ChangeStatusType;
  currentStatus: VehicleInspectionRequestType['status'];
}

export const ViewMediaComponent: React.FC<ViewMediaComponentProps> = ({
  artifactId,
  requestId,
  onChangeStatus,
  currentStatus,
}) => {
  const [data, setData] = useState<GetVehicleInspectionArtifactResponse | null>(
    null
  );
  const [open, setOpen] = useState(false);

  const { user } = useAuth0();

  const [, getVehicleInspectionArtifact] = useLazyApi<
    GetVehicleInspectionArtifactResponse
  >({
    method: 'GET',
    url: `/vehicleInspection/artifact/${artifactId}`,
  });

  if (!artifactId) {
    return null;
  }

  const VALID_STATUSES: Array<VehicleInspectionRequestType['status']> = [
    'OVERDUE',
    'SUBMITTED',
    'ESCALATE',
  ];

  const canChangeStatus =
    editVehicleInspectionPolicy(user!.effectiveGrants) &&
    VALID_STATUSES.includes(currentStatus);

  const onMediaClick = () => {
    getVehicleInspectionArtifact().then(({ data }): void => {
      setData(data);
      setOpen(true);
    });
  };

  const openVideo = () => {
    if (data?.url) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
    }
  };

  const openMap = () => {
    const latitude = data?.metadata.find(
      item => item.name === 'captureVINLatitude'
    )?.value;
    const longitude = data?.metadata.find(
      item => item.name === 'captureVINLongitude'
    )?.value;

    if (latitude && longitude) {
      window.open(
        `https://maps.google.com/?q=${latitude},${longitude}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const changeStatus = (status: AcceptedStatusType) => {
    if (canChangeStatus) {
      onChangeStatus(requestId, status);
      setOpen(false);
    }
  };

  return (
    <div>
      <button onClick={onMediaClick} className={styles.buttonLink}>
        <SharedAsset variant="playCircle" />
        Watch
      </button>
      <Modal
        variant="full"
        controlled={true}
        open={open}
        handleClose={() => setOpen(false)}
      >
        {() => (
          <>
            <ModalHeader className={styles.modalHeader}>
              Vehicle setup reviewer
            </ModalHeader>
            <ModalContent className={styles.modalContent}>
              {data?.partnerUrl ? (
                <iframe
                  style={{ width: '100%', height: '100%' }}
                  src={data.partnerUrl}
                  title="Vehicle setup reviewer"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : data?.url ? (
                <video
                  style={{ width: '100%', height: '100%' }}
                  controls
                  src={data.url}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <p>No media available</p>
              )}
            </ModalContent>
            <ModalFooter className={styles.modalFooterContainer}>
              <div className={styles.modalFooter}>
                <div>
                  <Button variant="secondary" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                </div>
                <div>
                  <Tooltip position="top" tooltip="Mark Vehicle Setup complete">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        changeStatus('COMPLETE');
                      }}
                      className={cx(styles.approveButton, styles.button)}
                      disabled={!canChangeStatus}
                    >
                      <Asset
                        icon={YesIcon}
                        description="Error"
                        height={16}
                        width={16}
                      />
                      Approve
                    </Button>
                  </Tooltip>
                  <Tooltip
                    position="top"
                    tooltip="Ask the customer to retake a poor quality video"
                  >
                    <Button
                      variant="danger"
                      onClick={() => {
                        changeStatus('OPEN');
                      }}
                      className={cx(styles.retakeButton, styles.button)}
                      disabled={!canChangeStatus}
                    >
                      <Asset
                        icon={ErrorIcon}
                        description="Error"
                        height={16}
                        width={16}
                      />
                      Retake
                    </Button>
                  </Tooltip>
                  <Tooltip
                    position="top"
                    tooltip="Request underwriting team review"
                  >
                    <Button
                      variant="secondary"
                      onClick={() => {
                        changeStatus('ESCALATE');
                      }}
                      className={cx(styles.escalateButton, styles.button)}
                      disabled={!canChangeStatus}
                    >
                      <Asset
                        icon={WarningIcon}
                        description="Error"
                        height={16}
                        width={16}
                      />
                      Escalate
                    </Button>
                  </Tooltip>
                </div>
                <div>
                  {data?.partnerUrl && (
                    <Button
                      variant="secondary"
                      onClick={() => openVideo()}
                      className={cx(styles.button)}
                    >
                      <SharedAsset variant="playCircle" />
                      View Video
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => openMap()}
                    className={cx(styles.button)}
                  >
                    <SharedAsset variant="outward" />
                    View on Map
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
};
